import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, RotateCcw, Home, Star, Info, Eye } from 'lucide-react';
import { API_BASE_URL } from '../config';

const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

const QuizGame = ({ config, onEnd }) => {
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [score, setScore] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const [clueLevel, setClueLevel] = useState(0);
    const [showRetryMessage, setShowRetryMessage] = useState(false);

    const initGame = useCallback(() => {
        if (!config || !config.questions || config.questions.length === 0) return;
        const storageKey = `ergo_played_${config.title || 'default'}`;
        let playedQuestions = JSON.parse(localStorage.getItem(storageKey) || '[]');
        let available = config.questions.filter(q => !playedQuestions.includes(q.question || q.answer));

        if (available.length < 5) {
            available = config.questions;
            playedQuestions = [];
            localStorage.setItem(storageKey, JSON.stringify([]));
        }

        const selection = shuffleArray(available).slice(0, 5);
        const newPlayed = [...playedQuestions, ...selection.map(q => q.question || q.answer)];
        localStorage.setItem(storageKey, JSON.stringify(newPlayed));

        setQuestions(selection);
        setCurrentQuestionIndex(0);
        setScore(0);
        setSelectedOption(null);
        setIsCorrect(null);
        setShowExplanation(false);
        setClueLevel(0);
    }, [config]);

    useEffect(() => { initGame(); }, [initGame]);

    if (!questions || questions.length === 0) return <div className="game-loading">Chargement...</div>;

    const question = questions[currentQuestionIndex];
    const isFinished = currentQuestionIndex >= questions.length;
    const isClueGame = config.type === 'progressive_clue';
    const maxScore = isClueGame ? questions.length * 3 : questions.length;

    const handleOptionClick = (option) => {
        if (selectedOption && isCorrect) return; // Bloque si déjà réussi

        const correct = option.toLowerCase() === question.answer.toLowerCase();
        setSelectedOption(option);
        setIsCorrect(correct);

        if (correct) {
            const points = isClueGame ? (3 - clueLevel) : 1;
            setScore(s => s + points);
            setShowExplanation(true);
            setShowRetryMessage(false);
        } else {
            // Logique de réessai
            setShowRetryMessage(true);
            setTimeout(() => {
                setShowRetryMessage(false);
                setSelectedOption(null);
                setIsCorrect(null);
            }, 2000);
        }
    };

    const handleNext = () => {
        setShowExplanation(false);
        setSelectedOption(null);
        setIsCorrect(null);
        setClueLevel(0);
        setCurrentQuestionIndex(prev => prev + 1);
    };

    const revealClue = () => {
        if (question.clues && clueLevel < question.clues.length - 1) {
            setClueLevel(clueLevel + 1);
        }
    };

    if (isFinished) {
        const percentage = (score / maxScore) * 100;
        let stars = percentage > 80 ? 3 : percentage > 40 ? 2 : 1;
        let message = stars === 3 ? "Excellent !" : stars === 2 ? "Bravo !" : "Bien essayé !";

        return (
            <div className="game-end-card">
                <div className="stars-container">
                    {[...Array(3)].map((_, i) => (
                        <Star key={i} className={i < stars ? "star filled" : "star"} size={48} />
                    ))}
                </div>
                <h2 className="end-message">{message}</h2>
                <div className="final-score-box">Score : <span className="score-number">{score} / {maxScore}</span></div>
                <div className="end-actions">
                    <button onClick={initGame} className="btn-replay"><RotateCcw /> Rejouer</button>
                    <button onClick={() => onEnd(null)} className="btn-home"><Home /> Accueil</button>
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-container">
            <div className="game-stats-header">
                <span>Question {currentQuestionIndex + 1} / {questions.length}</span>
                <span>Score: {score}</span>
            </div>

            <div className="question-media-area">
                {question.image && (
                    <div className="question-image-wrapper">
                        <img 
                            src={question.image.startsWith('http') ? question.image : `${API_BASE_URL}${question.image}`} 
                            alt="Question" className="question-image"
                            onError={(e) => { e.target.src = "https://placehold.co/400x300?text=Image+introuvable"; }}
                        />
                    </div>
                )}

                {isClueGame ? (
                    <div className="clue-game-box">
                        <div className="current-clue-text">
                            "{question.clues[clueLevel]}"
                        </div>
                        {!isCorrect && clueLevel < question.clues.length - 1 && (
                            <button onClick={revealClue} disabled={selectedOption !== null} className="btn-reveal-clue">
                                <Eye size={24} /> Indice suivant (-1 pt)
                            </button>
                        )}
                    </div>
                ) : (
                    <h2 className="question-text">{question.question}</h2>
                )}
            </div>

            {showRetryMessage && (
                <div className="retry-message">
                    <XCircle size={20} /> Mauvaise réponse <br/> Essayez encore !
                </div>
            )}

            <div className="options-grid">
                {question.options.map((option, index) => (
                    <button
                        key={index}
                        onClick={() => handleOptionClick(option)}
                        disabled={selectedOption !== null && isCorrect} // On ne bloque que si c'est déjà bon
                        className={`option-button ${selectedOption === option ? (isCorrect ? "correct" : "incorrect") : ""}`}
                    >
                        {option}
                        {selectedOption === option && (
                            <div className="feedback-icon">
                                {isCorrect ? <CheckCircle size={28} /> : <XCircle size={28} />}
                            </div>
                        )}
                    </button>
                ))}
            </div>

            {showExplanation && (
                <div className="explanation-area">
                    <div className="explanation-card">
                        <div className="explanation-header"><Info size={24} /> Le saviez-vous ?</div>
                        <p className="explanation-text">{question.explanation || `Bravo !`}</p>
                    </div>
                    <button onClick={handleNext} className="btn-next-question">Question Suivante</button>
                </div>
            )}
        </div>
    );
};

export default QuizGame;